import { useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { AlertCircleIcon, EllipsisVerticalIcon, PlusIcon } from 'lucide-react'
import {
	Alert, AlertDescription, AlertTitle,
	Badge,
	Button,
	ConfirmDialog,
	DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
	IconButton,
	Spinner,
	Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@rentflowdev/ui'
import { useDeleteRoleMutation, useListRolesQuery } from '@/api'
import { RoleDialog } from '@/components/role-dialog'
import { organizationSegmentLabel } from '@/lib/account-labels'
import { extractErrorMessage } from '@/lib/error-message'
import type { RoleDefinition } from '@/types'

export const Route = createFileRoute('/_app/admin/roles')({
	component: AdminRolesPage,
})

/**
 * Roles organizations give their members, for the platform's super admin
 * (ADR 0003). The gateway refuses everybody else, and so does this page's
 * query, which then shows the error.
 */
function AdminRolesPage() {
	const { t } = useTranslation()
	const { data: roles, isLoading, error } = useListRolesQuery()
	const [deleteRole, { isLoading: isDeleting }] = useDeleteRoleMutation()
	const [dialog, setDialog] = useState<{ role: RoleDefinition | null } | null>(null)
	const [confirmDelete, setConfirmDelete] = useState<RoleDefinition | null>(null)
	const [actionError, setActionError] = useState<string | null>(null)

	const items = roles ?? []
	const nextPosition = items.reduce((max, role) => Math.max(max, role.position + 1), 0)

	async function handleDelete(role: RoleDefinition) {
		setActionError(null)
		try {
			await deleteRole(role.key).unwrap()
		} catch (err) {
			setActionError(extractErrorMessage(err) ?? '')
		}
	}

	return (
		<div className="flex h-full flex-col gap-4 px-4 py-4 overflow-hidden">
			<div className="flex flex-wrap items-center justify-between gap-3">
				<p className="text-sm text-(--color-muted-foreground)">{t('admin.roles.description')}</p>
				<Button size="sm" onClick={() => setDialog({ role: null })} disabled={!roles}>
					<PlusIcon className="size-4" />
					{t('admin.roles.add')}
				</Button>
			</div>

			{actionError !== null && (
				<Alert variant="destructive">
					<AlertCircleIcon className="size-4" />
					<AlertTitle>{t('admin.roles.errors.delete')}</AlertTitle>
					{actionError && <AlertDescription>{actionError}</AlertDescription>}
				</Alert>
			)}

			{isLoading ? (
				<div className="flex flex-1 items-center justify-center"><Spinner /></div>
			) : error ? (
				<Alert variant="destructive">
					<AlertCircleIcon className="size-4" />
					<AlertTitle>{t('admin.roles.loadError')}</AlertTitle>
					<AlertDescription>{extractErrorMessage(error)}</AlertDescription>
				</Alert>
			) : items.length === 0 ? (
				<p className="text-sm text-(--color-muted-foreground)">{t('admin.roles.empty')}</p>
			) : (
				<div className="flex-1 overflow-auto rounded-md border border-(--color-border)">
					<Table>
						<TableHeader>
							<TableRow>
								<TableHead className="px-4">{t('admin.roles.columns.role')}</TableHead>
								<TableHead className="px-4">{t('admin.roles.columns.segments')}</TableHead>
								<TableHead className="px-4">{t('admin.roles.columns.members')}</TableHead>
								<TableHead className="w-[48px] px-4" />
							</TableRow>
						</TableHeader>
						<TableBody>
							{items.map((role) => (
								<TableRow key={role.key}>
									<TableCell className="px-4">
										<div className="flex flex-col gap-0.5">
											<span className="font-medium">{role.name}</span>
											<span className="font-mono text-xs text-(--color-muted-foreground)">{role.key}</span>
											{role.description && (
												<span className="text-sm text-(--color-muted-foreground)">{role.description}</span>
											)}
										</div>
									</TableCell>
									<TableCell className="px-4">
										<div className="flex flex-wrap gap-1">
											{role.system ? (
												<Badge variant="muted" size="sm">{t('admin.roles.system')}</Badge>
											) : role.segments.length === 0 ? (
												<span className="text-sm text-(--color-muted-foreground)">{t('admin.roles.noSegments')}</span>
											) : (
												role.segments.map((segment) => (
													<Badge key={segment} variant="secondary" size="sm">
														{organizationSegmentLabel(t, segment)}
													</Badge>
												))
											)}
										</div>
									</TableCell>
									<TableCell className="px-4 text-sm">{role.memberCount}</TableCell>
									<TableCell className="w-[48px] px-4">
										<DropdownMenu>
											<DropdownMenuTrigger render={<IconButton variant="ghost" size="sm" aria-label="Actions" />}>
												<EllipsisVerticalIcon className="size-4" />
											</DropdownMenuTrigger>
											<DropdownMenuContent align="end" className="min-w-52">
												<DropdownMenuItem onClick={() => setDialog({ role })}>
													{role.system ? t('admin.roles.actions.view') : t('admin.roles.actions.edit')}
												</DropdownMenuItem>
												{!role.system && (
													<DropdownMenuItem
														className="text-(--color-destructive) focus:text-(--color-destructive)"
														disabled={role.memberCount > 0}
														onClick={() => setConfirmDelete(role)}
													>
														{role.memberCount > 0 ? t('admin.roles.actions.inUse') : t('admin.roles.actions.delete')}
													</DropdownMenuItem>
												)}
											</DropdownMenuContent>
										</DropdownMenu>
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</div>
			)}

			<RoleDialog
				open={dialog !== null}
				onOpenChange={(next) => { if (!next) setDialog(null) }}
				role={dialog?.role ?? null}
				nextPosition={nextPosition}
			/>

			<ConfirmDialog
				open={confirmDelete !== null}
				onOpenChange={(next) => { if (!next) setConfirmDelete(null) }}
				title={t('admin.roles.deleteDialog.title')}
				description={t('admin.roles.deleteDialog.description', { name: confirmDelete?.name ?? '' })}
				cancelLabel={t('common.cancel')}
				confirmLabel={t('admin.roles.actions.delete')}
				variant="destructive"
				loading={isDeleting}
				onConfirm={() => {
					if (confirmDelete) void handleDelete(confirmDelete)
					setConfirmDelete(null)
				}}
			/>
		</div>
	)
}
